import { useTheme } from "../../context/ThemeContext";
import { dateDifference } from "../../utils";
import { formatAmount } from "../../utils";
import { IDeposit } from "../../utils/types";
import { useState } from "react";
import { ChevronRightIcon, ChevronLeftIcon } from "../SVGIcons";
import LinearBorderContainer from "../LinearBorderContainer";

export const DepositsHistoryTable: React.FC<{ depositHistory: Array<IDeposit> }> = ({ depositHistory }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const { theme } = useTheme();

  // Sort depositHistory by created_at in descending order
  const sortedHistory = [...(depositHistory || [])].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
  );

  const totalPages = Math.ceil(sortedHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDeposits = sortedHistory.slice(startIndex, endIndex);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newSize: number) => {
    setItemsPerPage(newSize);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  return (
    <LinearBorderContainer className="relative transition-colors duration-300">

      <h2 className="text-xl text-black dark:text-white font-borna px-6 py-4">Deposit History</h2>
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-[rgba(0,0,0,0.06)] dark:divide-white/20">
          <thead className="">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-black/50 dark:text-white/50 tracking-wider text-borna">Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-black/50 dark:text-white/50 tracking-wider text-borna">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-black/50 dark:text-white/50 tracking-wider text-borna">Amount</th>
            </tr>
          </thead>
          <tbody className="">
            {currentDeposits.map((deposit: IDeposit, index: number) => (
              <tr key={index} className={`${deposit.is_deposit ? '' : ''} font-borna hover:bg-[#F2F2F2] dark:hover:bg-[#262626]`}>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-black dark:text-white">{dateDifference(deposit.created_at)} ago</td>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-black dark:text-white">{deposit.is_deposit ? 'Deposit' : 'Withdraw'}</td>
                <td className="px-6 py-2 whitespace-nowrap text-sm text-black dark:text-white">τ{formatAmount(deposit.deposit_amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-4 gap-3 sm:gap-0">
          <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-2 sm:space-y-0 sm:space-x-4 w-auto">
            <span className="text-sm text-black/50 dark:text-white/50">
              Showing {startIndex + 1} to {Math.min(endIndex, sortedHistory.length)} of {sortedHistory.length} entries
            </span>
            <div className="flex items-center space-x-2 w-full sm:w-auto">
              <span className="text-sm text-black/50 dark:text-white/50">Show</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className={`px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm w-20 ${theme === 'dark' ? 'dark' : ''}`}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="text-sm text-black/50 dark:text-white/50">entries</span>
            </div>
          </div>
          <div className="flex items-center space-x-2 w-full sm:w-auto justify-center sm:justify-end mt-2 sm:mt-0 overflow-x-auto">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-0 py-0 text-sm sm:px-3 sm:py-1 sm:text-base rounded-md hover:bg-black/10 dark:hover:bg-white/10 ${currentPage === 1
                && 'text-black/50 dark:text-white/50 cursor-not-allowed'
                }`}
            >
              <ChevronLeftIcon fillColor={currentPage === 1 ? theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' : theme === 'dark' ? 'white' : 'black'} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={`px-0 py-0 text-sm sm:px-3 sm:py-1 sm:text-base rounded-md ${currentPage === page
                  ? 'bg-[#00FFEE] text-black'
                  : 'text-black/50 dark:text-white/50 hover:bg-black/10 dark:hover:bg-white/10'
                  }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-0 py-0 text-sm sm:px-3 sm:py-1 sm:text-base rounded-md hover:bg-black/10 dark:hover:bg-white/10 ${currentPage === totalPages
                && 'text-black/50 dark:text-white/50 cursor-not-allowed'
                }`}
            >
              <ChevronRightIcon fillColor={currentPage === totalPages ? theme === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' : theme === 'dark' ? 'white' : 'black'} />
            </button>
          </div>
        </div>
      )}
    </LinearBorderContainer>
  );
};
